import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DashboardStatsResponse, RequestsResponse } from '@shared/api-responses';
import { ORDER_STATUS_LABELS, type OrderStatus } from '@shared/types';
import { apiFetch } from '../hooks/useApi';

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    apiFetch<DashboardStatsResponse>('/dashboard/stats')
      .then(setStats)
      .catch(() => setError(true));
  }, []);

  useEffect(() => load(), [load]);

  /**
   * Requests left over from before orders were created on submit (or whose
   * order step failed). Each becomes its own order, one call per request, so
   * stations are never combined -- the same thing submitting does now.
   */
  async function convertWaiting() {
    setBusy(true);
    setMsg(null);
    try {
      const { requests } = await apiFetch<RequestsResponse>('/requests?status=open');
      let done = 0;
      for (const r of requests) {
        try {
          await apiFetch('/consolidate', { method: 'POST', body: { requestIds: [r.id] } });
          done++;
        } catch {
          // Someone else got to it first; carry on with the rest.
        }
      }
      setMsg(`Turned ${done} request${done === 1 ? '' : 's'} into orders.`);
    } catch {
      setMsg('Could not load the waiting requests.');
    } finally {
      setBusy(false);
      load();
    }
  }

  if (error) return <div className="p-6 text-ems-red">Failed to load dashboard.</div>;
  if (!stats) return <div className="p-6 text-zinc-400">Loading…</div>;

  const tiles = [
    { label: 'New orders', value: stats.newOrders },
    { label: 'To fulfill', value: stats.awaitingFulfillment },
    { label: 'Orders (all)', value: stats.ordersByStatus.reduce((n, s) => n + s.count, 0) },
  ];

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <h1 className="text-xl font-bold text-white mb-4">Dashboard</h1>

      {msg && <div className="mb-4 rounded-lg bg-surface-raised px-3 py-2 text-sm text-zinc-200">{msg}</div>}

      {stats.openRequests > 0 && (
        <div className="glass mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl p-4">
          <p className="text-sm text-zinc-200">
            {stats.openRequests} request{stats.openRequests === 1 ? ' is' : 's are'} still waiting to become an order.
          </p>
          <button onClick={convertWaiting} disabled={busy} className="btn-primary min-h-[44px]">
            {busy ? 'Working…' : 'Turn into orders'}
          </button>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 mb-6">
        {tiles.map((t) => (
          <Link key={t.label} to="/orders" className="glass rounded-xl p-4">
            <div className="text-3xl font-bold text-white">{t.value}</div>
            <div className="text-xs text-zinc-400 mt-1">{t.label}</div>
          </Link>
        ))}
      </div>
      <h2 className="text-sm font-semibold text-zinc-300 mb-2">Orders by status</h2>
      <div className="glass rounded-xl p-4 space-y-1">
        {stats.ordersByStatus.length === 0 && <p className="text-sm text-zinc-500">No orders yet.</p>}
        {stats.ordersByStatus.map((s) => (
          <div key={s.status} className="flex justify-between text-sm text-white">
            <span>{ORDER_STATUS_LABELS[s.status as OrderStatus] ?? s.status}</span>
            <span className="font-mono">{s.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
